import { useEffect, useMemo, useState } from "react";
import { api, eur } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TrendingUp, Store, Info } from "lucide-react";

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

// btw-tariefetiketten (zelfde codes als backend RATE_CODES)
const RATE_LABELS = {
  E: "Tabak (0%)",
  "0": "Lotto (0%)",
  "6": "Voeding | Tijdschriften (6%)",
  "21": "Alcohol | E-Cigarettes (21%)",
};
const RATE_ORDER = ["E", "0", "6", "21"];

function SplitTable({ perRate, total, testid }) {
  const rows = RATE_ORDER.filter((c) => perRate[c]).map((c) => ({ code: c, ...perRate[c] }));
  return (
    <Table>
      <TableHeader><TableRow>
        <TableHead>Btw-tarief</TableHead>
        <TableHead className="text-right">Zonder btw</TableHead>
        <TableHead className="text-right">Btw</TableHead>
        <TableHead className="text-right">Met btw</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.code} data-testid={`${testid}-rate-${r.code}`}>
            <TableCell>{RATE_LABELS[r.code] || r.code}</TableCell>
            <TableCell className="text-right tabular-nums">{eur(r.base)}</TableCell>
            <TableCell className="text-right tabular-nums">{eur(r.vat)}</TableCell>
            <TableCell className="text-right tabular-nums font-medium">{eur(r.gross)}</TableCell>
          </TableRow>
        ))}
        <TableRow className="bg-muted/40 font-semibold">
          <TableCell>Totaal</TableCell>
          <TableCell className="text-right tabular-nums" data-testid={`${testid}-total-base`}>{eur(total.base)}</TableCell>
          <TableCell className="text-right tabular-nums" data-testid={`${testid}-total-vat`}>{eur(total.vat)}</TableCell>
          <TableCell className="text-right tabular-nums text-emerald-700" data-testid={`${testid}-total-gross`}>{eur(total.gross)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

export default function RapportenOpbrengsten() {
  const [from, setFrom] = useState(firstOfYear());
  const [to, setTo] = useState(today());
  const [loc, setLoc] = useState("all");
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => setLocations([]));
  }, []);

  const load = () => {
    setLoading(true);
    api.get("/reports/period", { params: { start: from, end: to, location_id: loc } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const total = data?.total || { base: 0, vat: 0, gross: 0 };
  // Per vestiging: toon ALLE vestigingen (ook zonder ontvangsten -> €0)
  const perLocation = useMemo(() => {
    if (!data?.per_location) return [];
    const zero = () => RATE_ORDER.reduce((a, c) => ({ ...a, [c]: { base: 0, vat: 0, gross: 0 } }), {});
    const byName = {};
    // 1) alle actieve vestigingen uit de lijst (ook €0)
    for (const l of locations) {
      byName[l.name] = { name: l.name, perRate: data.per_location[l.name] || zero() };
    }
    // 2) eventuele namen die enkel in de data zitten (bv. hernoemde/verwijderde vestiging)
    for (const [name, perRate] of Object.entries(data.per_location)) {
      if (!byName[name]) byName[name] = { name, perRate };
    }
    return Object.values(byName).map((L) => {
      const t = RATE_ORDER.reduce((a, c) => {
        const v = L.perRate[c] || { base: 0, vat: 0, gross: 0 };
        return { base: a.base + v.base, vat: a.vat + v.vat, gross: a.gross + v.gross };
      }, { base: 0, vat: 0, gross: 0 });
      return { ...L, total: t };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [data, locations]);

  return (
    <div className="space-y-6" data-testid="rapporten-opbrengsten-page">
      <div>
        <p className="text-xs font-semibold tracking-widest text-emerald-700 uppercase">Uit het dagboek van ontvangsten</p>
        <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
          <TrendingUp className="h-7 w-7 text-emerald-600" /> Opbrengsten
        </h1>
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-emerald-50/60 border border-emerald-100 rounded-md p-3">
        <Info className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
        Alle geregistreerde dagontvangsten (incl. correcties) uit het dagboek, verdeeld in <b className="mx-1">zonder btw</b> (maatstaf),
        <b className="mx-1">btw</b> (verschuldigd) en <b className="mx-1">met btw</b> (bruto ontvangst).
      </div>

        <Card className="shadow-sm">
          <CardContent className="p-4 flex flex-wrap items-end gap-4">
            <div className="space-y-1.5"><Label>Van</Label><Input type="date" data-testid="opb-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Tot</Label><Input type="date" data-testid="opb-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Vestiging</Label>
              <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="opb-location"
                value={loc} onChange={(e) => setLoc(e.target.value)}>
                <option value="all">Alle vestigingen</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="opb-refresh" onClick={load}>Tonen</Button>
          </CardContent>
        </Card>

        {loading ? <div className="py-16 text-center text-muted-foreground">Laden…</div> : !data ? null : (
          <>
            <Card className="shadow-sm border-emerald-100">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-lg">Totaal opbrengsten</CardTitle>
                <span className="text-sm text-muted-foreground" data-testid="opb-entry-count">{data.entry_count} dagboekregistratie(s)</span>
              </CardHeader>
              <CardContent className="p-0">
                <SplitTable perRate={data.per_rate} total={total} testid="opb-all" />
              </CardContent>
            </Card>

            {loc === "all" && perLocation.length > 0 && (
              <div className="space-y-6">
                <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Store className="h-5 w-5 text-emerald-600" /> Per vestiging</h2>
                {perLocation.map((L) => (
                  <Card key={L.name} className="shadow-sm" data-testid={`opb-loc-${L.name.replace(/\s/g, "-")}`}>
                    <CardHeader className="pb-2 flex flex-row items-center justify-between">
                      <CardTitle className="font-heading text-base flex items-center gap-2"><Store className="h-4 w-4 text-emerald-600" /> {L.name}</CardTitle>
                      <span className="text-sm text-muted-foreground">Met btw: <b className="text-emerald-700">{eur(L.total.gross)}</b></span>
                    </CardHeader>
                    <CardContent className="p-0">
                      <SplitTable perRate={L.perRate} total={L.total} testid={`opb-loc-${L.name.replace(/\s/g, "-")}`} />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {data.entry_count === 0 && (
              <Card><CardContent className="py-16 text-center text-muted-foreground" data-testid="opb-empty">
                Geen dagontvangsten in deze periode.
              </CardContent></Card>
            )}
          </>
        )}
    </div>
  );
}
